import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import UnsupportedOSDialog from 'components/UnsupportedOSDialog';
import { useFeatures } from 'hooks/useFeatures';
import { getOSInfo, OSPlatform, type OSInfo } from 'utils/osVersion';

const UNSUPPORTED_OS_DISMISSED_KEY = 'nile_unsupported_os_dismissed_at';
const ONE_HOUR_MS = 60 * 60 * 1000;

const getDismissedAt = (): number | null => {
  try {
    const val = localStorage.getItem(UNSUPPORTED_OS_DISMISSED_KEY);
    return val ? parseInt(val, 10) : null;
  } catch {
    return null;
  }
};

const isDismissedWithinLastHour = (): boolean => {
  const dismissedAt = getDismissedAt();
  if (!dismissedAt) return false;
  return Date.now() - dismissedAt < ONE_HOUR_MS;
};

const setDismissedAt = (timestamp: number) => {
  try {
    localStorage.setItem(UNSUPPORTED_OS_DISMISSED_KEY, String(timestamp));
  } catch {
    // Ignore
  }
};

const UnsupportedOSContext = createContext<{
  osInfo: OSInfo;
  isUnsupported: boolean;
} | null>(null);

export const UnsupportedOSProvider = ({ children }: { children: React.ReactNode }) => {
  const { isEnabled } = useFeatures();
  const osInfo = useMemo(() => getOSInfo(), []);
  const [dismissed, setDismissed] = useState(isDismissedWithinLastHour);

  const showDialog =
    isEnabled('UnsupportedOSDialog') && osInfo.platform !== OSPlatform.Other && !osInfo.isSupported && !dismissed;

  const handleClose = useCallback(() => {
    setDismissed(true);
    setDismissedAt(Date.now());
  }, []);

  const value = useMemo(
    () => ({
      osInfo,
      isUnsupported: osInfo.platform !== OSPlatform.Other && !osInfo.isSupported,
    }),
    [osInfo],
  );

  return (
    <UnsupportedOSContext.Provider value={value}>
      {children}
      {showDialog && <UnsupportedOSDialog open onClose={handleClose} osInfo={osInfo} />}
    </UnsupportedOSContext.Provider>
  );
};

export const useUnsupportedOS = () => {
  const context = useContext(UnsupportedOSContext);
  return (
    context ?? {
      osInfo: getOSInfo(),
      isUnsupported: false,
    }
  );
};
